/*
  <nav-drawer> — mobile navigation drawer with drill-down levels.
  Markup: snippets/header-drawer.liquid. Motion: CSS transitions on the shared
  tokens (component-nav-drawer.css); this script toggles state classes, locks
  scrolling, traps focus and keeps the sticky header from hiding.
*/
if (!customElements.get('nav-drawer')) {
  customElements.define(
    'nav-drawer',
    class NavDrawer extends HTMLElement {
      connectedCallback() {
        this.opener = this.querySelector('.nav-drawer__open');
        this.panel = this.querySelector('.nav-drawer__panel');
        this.overlay = this.querySelector('.nav-drawer__overlay');
        this.root = this.querySelector('[data-nav-level="root"]');
        if (!this.opener || !this.panel || !this.root) return;
        this.levels = Array.from(this.querySelectorAll('.nav-drawer__level:not([data-nav-level="root"])'));
        this.headerWrapper = this.closest('.header-wrapper');
        this.isOpen = false;
        this.current = null;

        this.opener.addEventListener('click', () => this.open());
        this.querySelectorAll('[data-nav-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        this.querySelectorAll('[data-nav-open-level]').forEach((button) =>
          button.addEventListener('click', () => this.openLevel(button.dataset.navOpenLevel, button))
        );
        this.querySelectorAll('[data-nav-back]').forEach((button) => button.addEventListener('click', () => this.back()));

        this.panel.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.isOpen) return;
          event.preventDefault();
          if (this.current) this.back();
          else this.close();
        });

        // Leaving the mobile breakpoint while open: close instantly
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
        this.desktopQuery.addEventListener('change', (event) => {
          if (event.matches && this.isOpen) this.close(true);
        });

        // The panel is position: fixed; move it (and the overlay) to <body> so no
        // transformed/sticky header ancestor can offset or clip it. Done last, after
        // every listener above has been attached.
        document.body.appendChild(this.overlay);
        document.body.appendChild(this.panel);
      }

      disconnectedCallback() {
        // The theme editor re-renders the section: drop the detached panel/overlay
        if (this.panel && this.panel.parentNode === document.body) this.panel.remove();
        if (this.overlay && this.overlay.parentNode === document.body) this.overlay.remove();
        document.body.classList.remove('overflow-hidden');
      }

      duration(name) {
        return window.motion ? motion.duration(name) : 0;
      }

      // Runs `callback` after a transition of `name` duration (instant when reduced)
      after(name, callback) {
        const ms = this.duration(name);
        if (ms === 0) callback();
        else setTimeout(callback, ms);
      }

      open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.overlay.hidden = false;
        this.panel.hidden = false;
        this.opener.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        if (this.headerWrapper) this.headerWrapper.preventHide = true;
        // Force a style flush so the slide-in transitions from the hidden state
        void this.panel.offsetWidth;
        this.classList.add('is-open');
        this.panel.classList.add('is-open');
        this.overlay.classList.add('is-open');
        this.after('slow', () => {
          if (!this.isOpen) return;
          if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel.querySelector('.nav-drawer__close'));
        });
      }

      close(instant = false) {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        this.panel.classList.remove('is-open');
        this.overlay.classList.remove('is-open');
        this.opener.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        if (this.headerWrapper) this.headerWrapper.preventHide = false;
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
        const finish = () => {
          if (this.isOpen) return;
          this.panel.hidden = true;
          this.overlay.hidden = true;
          this.levels.forEach((level) => {
            level.hidden = true;
            level.classList.remove('is-active');
          });
          this.root.classList.remove('is-behind');
          this.root.removeAttribute('inert');
          this.current = null;
          this.panel.querySelectorAll('[data-nav-open-level]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
        };
        if (instant) finish();
        else this.after('medium', finish);
      }

      openLevel(handle, trigger) {
        const level = this.panel.querySelector(`.nav-drawer__level[data-nav-level="${handle}"]`);
        if (!level || this.current === level) return;
        this.current = level;
        this.lastTrigger = trigger;
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        level.hidden = false;
        const scroller = level.querySelector('.nav-drawer__scroll');
        if (scroller) scroller.scrollTop = 0;
        void level.offsetWidth;
        level.classList.add('is-active');
        this.root.classList.add('is-behind');
        this.after('medium', () => {
          if (this.current !== level) return;
          this.root.setAttribute('inert', '');
          const title = level.querySelector('.nav-drawer__title');
          if (title) title.focus({ preventScroll: true });
        });
      }

      back() {
        const level = this.current;
        if (!level) return;
        this.current = null;
        level.classList.remove('is-active');
        this.root.classList.remove('is-behind');
        this.root.removeAttribute('inert');
        if (this.lastTrigger) {
          this.lastTrigger.setAttribute('aria-expanded', 'false');
          this.lastTrigger.focus({ preventScroll: true });
        }
        this.after('medium', () => {
          if (!level.classList.contains('is-active')) level.hidden = true;
        });
      }
    }
  );
}
